import { useCallback, useEffect, useMemo, useState } from 'react';
import { auditService } from '../../services/auditService';
import { useAuth } from '../../context/AuthContext';
import { fmtQty } from '../Reports/ui';
import { PageLoader } from '../../components/Loader';
import Pager, { usePaged } from '../../components/Pager';

export const RECOUNT_STATUS = {
  open: { label: 'Waiting for auditor', tone: 'warn' },
  acknowledged: { label: 'Auditor on it', tone: 'info' },
  done: { label: 'Recounted', tone: 'good' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
};

function when(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

/**
 * Recount requests for one audit. Staff move them on (acknowledge / done /
 * cancel); a client can cancel requests they sent that are still open.
 */
function RecountPanel({ auditId, canManage, refreshKey, onNewRequest }) {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('active');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      setRows((await auditService.getRecounts(auditId)) || []);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [auditId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const shown = useMemo(() => {
    const list = rows || [];
    if (filter === 'active') return list.filter((r) => r.status === 'open' || r.status === 'acknowledged');
    if (filter === 'all') return list;
    return list.filter((r) => r.status === filter);
  }, [rows, filter]);

  const paged = usePaged(shown, { pageSize: 20, sizes: [20, 50, 100], resetKey: filter, storageKey: 'portal-recounts' });

  const counts = useMemo(() => {
    const list = rows || [];
    const c = { open: 0, acknowledged: 0, done: 0, cancelled: 0 };
    list.forEach((r) => {
      c[r.status] = (c[r.status] || 0) + 1;
    });
    return c;
  }, [rows]);

  const update = async (row, status) => {
    let note;
    if (status === 'done') {
      // eslint-disable-next-line no-alert
      note = window.prompt('Recount result / note for the client (optional):', '');
      if (note === null) return;
    }
    if (status === 'cancelled') {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Cancel this recount request?')) return;
    }
    setBusy(row.id);
    setError('');
    try {
      await auditService.updateRecount(auditId, row.id, { status, resolution_note: note || undefined });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="audit-portal__panel live-panel">
      <header className="live-panel__header">
        <div>
          <h2>Recount requests</h2>
          <p className="live-muted">
            Sent to the auditors' phones. They mark a request "on it" when they start and "done"
            once the new count is scanned.
          </p>
        </div>
        <button type="button" className="compare-button live-button--warn" onClick={() => onNewRequest({})}>
          + New recount request
        </button>
      </header>

      <div className="live-chips" role="group" aria-label="Show">
        {[
          ['active', `Active (${fmtQty(counts.open + counts.acknowledged)})`],
          ['done', `Recounted (${fmtQty(counts.done)})`],
          ['cancelled', `Cancelled (${fmtQty(counts.cancelled)})`],
          ['all', `All (${fmtQty((rows || []).length)})`],
        ].map(([id, label]) => (
          <button key={id} type="button" aria-pressed={filter === id} onClick={() => setFilter(id)}>
            {label}
          </button>
        ))}
      </div>

      {error ? <p className="compare-error">{error}</p> : null}

      {rows === null ? (
        <PageLoader label="Loading recount requests…" />
      ) : shown.length === 0 ? (
        <p className="compare-empty">
          {filter === 'active' ? 'No recount requests waiting.' : 'Nothing to show.'}
        </p>
      ) : (
        <>
        <ul className="live-recounts" ref={paged.anchorRef}>
          {paged.items.map((row) => {
            const status = RECOUNT_STATUS[row.status] || RECOUNT_STATUS.open;
            const mine = String(row.requested_by) === String(user?.id);
            const active = row.status === 'open' || row.status === 'acknowledged';
            return (
              <li key={row.id} className={`live-recount live-recount--${status.tone}`}>
                <div className="live-recount__main">
                  <div className="live-recount__title">
                    <span className={`live-pill live-pill--${status.tone}`}>{status.label}</span>
                    {row.location_code ? <strong>{row.location_code}</strong> : null}
                    {row.ean ? (
                      <span>
                        {row.product_name ? `${row.product_name} · ` : ''}
                        <code>{row.ean}</code>
                      </span>
                    ) : null}
                  </div>
                  {row.note ? <p className="live-recount__note">“{row.note}”</p> : null}
                  <p className="live-muted">
                    Requested by {row.requested_by_name || 'someone'}
                    {row.requested_role === 'client' ? ' (client)' : ''} · {when(row.created_at)}
                    {row.acknowledged_at
                      ? ` · taken by ${row.acknowledged_by_name || 'auditor'} ${when(row.acknowledged_at)}`
                      : ''}
                    {row.resolved_at
                      ? ` · ${row.status === 'done' ? 'done' : 'cancelled'} by ${row.resolved_by_name || '—'} ${when(row.resolved_at)}`
                      : ''}
                  </p>
                  {row.resolution_note ? (
                    <p className="live-recount__result">Result: {row.resolution_note}</p>
                  ) : null}
                </div>
                {active ? (
                  <div className="live-recount__actions">
                    {canManage && row.status === 'open' ? (
                      <button type="button" className="compare-button compare-button--ghost" disabled={busy === row.id} onClick={() => update(row, 'acknowledged')}>
                        Mark “on it”
                      </button>
                    ) : null}
                    {canManage ? (
                      <button type="button" className="compare-button" disabled={busy === row.id} onClick={() => update(row, 'done')}>
                        Mark done
                      </button>
                    ) : null}
                    {canManage || (mine && row.status === 'open') ? (
                      <button type="button" className="compare-link" disabled={busy === row.id} onClick={() => update(row, 'cancelled')}>
                        Cancel
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
        <Pager paged={paged} noun="requests" compact />
        </>
      )}
    </section>
  );
}

export default RecountPanel;
